import React from "react";
import { useState, useEffect, useContext, useRef } from "react";
import {
  ALERT_TIMEOUT,
  MIN_AI_TEXT_LENGTH,
  STATUS_CHECK_INTERVAL,
} from "../Config";
import { ThemeButtonBGAIDetector } from "../Config";
import { REQUEST_URL } from "../Config";
import { aiDetectorContext } from "./AIDetectorMain";
import { formatSecondsToMS } from "../Utils";
import { useNavigate } from "react-router";
import { LoggedInContext } from "../../App";

function AIDetector() {
  //navigate
  const navigate = useNavigate(); //react router navigate

  //time to show time value
  //initialise with 2 minutes
  const [time, setTime] = useState(60 * 2);

  //get ai detector values
  const {
    aiDetectorTextsValues,
    setAIDetectorTextsValues,
    detectorResultSectionRef,
  } = useContext(aiDetectorContext);

  //alert state
  const [alert, setAlert] = useState({
    type: "success",
    message: "",
    visible: false,
  });

  //
  //get ai detector values
  // eslint-disable-next-line no-unused-vars
  const { maxAITextLength } = useContext(LoggedInContext);

  //delay time
  //delay to run the code in runpod server i.e in queue time
  //execution time
  //time taken to execute the code
  //total time is = delayTime + executionTime
  var process_time = useRef({
    delayTime: 0.0,
    executionTime: 0.0,
  });

  //submit form button clicked state
  const [submitClicked, setSubmitClicked] = useState(false);
  //result loadedstate
  const [resultLoaded, setResultLoaded] = useState(false);

  //scroll to section on result
  const scrollToSection = () => {
    if (detectorResultSectionRef.current) {
      detectorResultSectionRef.current.scrollIntoView({
        behavior: "smooth", // Optional: for smooth scrolling animation
        block: "start", // Optional: aligns the top of the element with the top of the viewport
      });
    }
  };

  //timer id
  const timerId = useRef();

  //use effect to handle the submit button click
  useEffect(() => {
    // If the submit button was clicked
    if (submitClicked) {
      //reset
      process_time.delayTime = 0.0;
      process_time.executionTime = 0.0;

      //call api to ai detector
      aiDetectorAPICall();
      //time for time remaiming
      timerId.current = setInterval(
        () => setTime((time) => Math.max(0, time - 1)),
        1000
      );
    } else {
      //clear
      clearInterval(timerId.current);
      //reset
      setTime(60 * 2);
    }

    //if result loaded
    if (resultLoaded === true) {
      //update search history
      updateSearchHistory();
    }

    //cleanup
    return () => clearInterval(timerId); // Cleanup on unmount

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitClicked, resultLoaded]);

  //check status from status from endpoint
  const checkRunpodJobStatus = async (job_id) => {
    //get the access token from local storage
    var accessToken = localStorage.getItem("access_token");
    accessToken =
      accessToken !== null && accessToken !== "undefined" ? accessToken : "";

    //fetch the status from the API
    await fetch(REQUEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`, // if needed
      },
      body: JSON.stringify({
        request_type: "STATUS_CHECK_AI_DETECTOR",
        text_to_process: "",
        job_id: job_id, // can be used for tracking jobs
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        //check success
        if (data.success === true) {
          //check status
          if (data.status === "COMPLETED") {
            //hide the spinner
            setSubmitClicked(false);

            //result json
            const result = JSON.parse(data.message);
            // Update the state with the new value
            setAIDetectorTextsValues({
              ...aiDetectorTextsValues,
              probability:
                result.probability *
                (aiDetectorTextsValues.mode === 3
                  ? 1
                  : aiDetectorTextsValues.mode === 2
                  ? 0.85
                  : 0.7), //change probability w.r.t moderaton level casual, moderate, strict
              is_ai_generated:
                result.probability *
                  (aiDetectorTextsValues.mode === 3
                    ? 1
                    : aiDetectorTextsValues.mode === 2
                    ? 0.85
                    : 0.7) >
                0.5
                  ? 1
                  : 0,
              text: result.text,
              isLoaded: true,
              detection_sentences: result.detection_sentences.map((item) => ({
                ...item,
                probability:
                  item.probability *
                  (aiDetectorTextsValues.mode === 3
                    ? 1
                    : aiDetectorTextsValues.mode === 2
                    ? 0.85
                    : 0.7),
              })),
            });

            //set time
            process_time.delayTime = data.delay_time;
            process_time.executionTime = data.execution_time;

            //scroll
            scrollToSection();
            //set
            setResultLoaded(true);
          } else if (
            data.status === "FAILED" ||
            data.status === "CANCELLED" ||
            data.status === "TIMED_OUT"
          ) {
            //hide the spinner
            setSubmitClicked(false);

            //set alert state to show error
            setAlert({
              type: "danger",
              message: "Failed to detect text. Please try again.",
              visible: true,
            });
            //hide alert after 5 seconds
            setTimeout(() => {
              setAlert({ ...alert, visible: false });
            }, ALERT_TIMEOUT);
          } else if (
            data.status === "IN_QUEUE" ||
            data.status === "RUNNING" ||
            data.status === "IN_PROGRESS"
          ) {
            //check api after 10 seconds
            setTimeout(() => {
              //callback
              checkRunpodJobStatus(job_id);
            }, STATUS_CHECK_INTERVAL);
          }
        } else {
          //hide the spinner
          setSubmitClicked(false);
          //scroll to top
          window.scrollTo(0, 0);
          //set alert state to show error
          setAlert({
            type: "danger",
            message: data.message || "Failed to detect text. Please try again.",
            visible: true,
          });
          //hide alert after 5 seconds
          setTimeout(() => {
            setAlert({ ...alert, visible: false });
          }, ALERT_TIMEOUT);
        }

        console.log("Response Data ai detector:", data);
      })
      .catch((error) => {
        console.error("Error:", error);
        //hide the spinner
        setSubmitClicked(false);

        //set alert state to show error
        setAlert({
          type: "danger",
          message: "Failed to detect text. Please try again.",
          visible: true,
        });
        //hide alert after 5 seconds
        setTimeout(() => {
          setAlert({ ...alert, visible: false });
        }, ALERT_TIMEOUT);
      });
  };

  //ai detect texts from endpoint
  const aiDetectorAPICall = async () => {
    //get the access token from local storage
    var accessToken = localStorage.getItem("access_token");
    accessToken =
      accessToken !== null && accessToken !== "undefined" ? accessToken : "";

    //fetch the humanized text from the API
    await fetch(REQUEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`, // if needed
      },
      body: JSON.stringify({
        request_type: "AI_DETECTOR",
        text_to_process: aiDetectorTextsValues.enter_ai_text,
        mode: aiDetectorTextsValues.mode,
        job_id: null, // can be used for tracking jobs
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        //set token in local storage
        localStorage.setItem("access_token", data.token);

        //check if the response is successful
        //and job is send to runpod server
        if (data.success === true) {
          //set job_id
          const job_id = data.job_id;
          //check response from runpod server
          //when ai detection text is completed
          if (
            data.status === "FAILED" ||
            data.status === "CANCELLED" ||
            data.status === "TIMED_OUT" ||
            job_id === null
          ) {
            //hide the spinner
            setSubmitClicked(false);

            //set alert state to show error
            setAlert({
              type: "danger",
              message: "Failed to detect text. Please try again.",
              visible: true,
            });
            //hide alert after 5 seconds
            setTimeout(() => {
              setAlert({ ...alert, visible: false });
            }, ALERT_TIMEOUT);
          } else {
            //check status for job_id
            checkRunpodJobStatus(job_id);
          }
        } else {
          //hide the spinner
          setSubmitClicked(false);
          //scroll to top
          window.scrollTo(0, 0);
          //set alert state to show error
          setAlert({
            type: "danger",
            message: data.message || "Failed to detect text. Please try again.",
            visible: true,
          });
          //hide alert after 5 seconds
          setTimeout(() => {
            setAlert({ ...alert, visible: false });
          }, ALERT_TIMEOUT);
        }

        console.log("Response Data ai detector:", data);
      })
      .catch((error) => {
        console.error("Error:", error);
        //hide the spinner
        setSubmitClicked(false);
        //set alert state to show error
        setAlert({
          type: "danger",
          message:
            "Failed to detect text. Please try again. \n" + error.message,
          visible: true,
        });

        //hide
        setTimeout(() => {
          //hide alert after 5 seconds
          setAlert({ ...alert, visible: false });
        }, ALERT_TIMEOUT);
      });
  };

  //update search history
  async function updateSearchHistory() {
    //get the access token from local storage
    var accessToken = localStorage.getItem("access_token");
    accessToken =
      accessToken !== null && accessToken !== "undefined" ? accessToken : "";

    //fetch the humanized text from the API
    await fetch(REQUEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`, // if needed
      },
      body: JSON.stringify({
        request_type: "INSERT_SEARCH_TO_HISTORY",
        text_searched: aiDetectorTextsValues.enter_ai_text,
        text_result: `${(aiDetectorTextsValues.probability * 100).toFixed(2)}`,
        delay_time: process_time.delayTime,
        execution_time: process_time.executionTime,
        search_type: "AI_DETECTOR",
      }),
    })
      .then((result) => result.json())
      .then((data) => {
        //reset
        setResultLoaded(false);

        console.log("Search history result: ", data);
      })
      .catch((error) => {
        //reset
        setResultLoaded(false);
      });

    console.log("Update Search History");
  }

  //submit handler for the form
  const onSubmitAIDetectorTextsForm = (event) => {
    // Get the textarea value
    event.preventDefault();

    //check if empty string
    if (aiDetectorTextsValues.enter_ai_text.length <= MIN_AI_TEXT_LENGTH) {
      //show error
      //set alert state to show error
      setAlert({
        type: "danger",
        message: "Too short text. Please enter at least 40 characters.",
        visible: true,
      });
      //hide alert after 5 seconds
      setTimeout(() => {
        setAlert({ ...alert, visible: false });
      }, ALERT_TIMEOUT);
      //return
      return;
    }

    //check text count
    if (aiDetectorTextsValues.enter_ai_text.length > maxAITextLength) {
      //show warning
      //set alert state to show error
      setAlert({
        type: "warning",
        message:
          "Max character limit exceeded. Input texts will be trimmed before processing.",
        visible: true,
      });
      //hide alert after 5 seconds
      setTimeout(() => {
        setAlert({ ...alert, visible: false });
      }, ALERT_TIMEOUT);

      // Update the state with the new value
      setAIDetectorTextsValues({
        ...aiDetectorTextsValues,
        enter_ai_text: aiDetectorTextsValues.enter_ai_text.substring(
          0,
          maxAITextLength
        ),
      });
    }

    //set the submit clicked state to true
    //can not access the form values directly here
    //as the state is not updated immediately
    //use effect will be called after the state is updated
    setSubmitClicked(true);
  };

  //onvalue change handler for the textarea
  const onChangeAIDetectorTextsForm = (event) => {
    // Update the state with the new value
    setAIDetectorTextsValues({
      ...aiDetectorTextsValues,
      [event.target.id]: event.target.value,
    });
  };

  return (
    <section id="ai_detector">
      <div className="p-3">
        <div className="container">
          <div style={{ marginTop: "6rem" }}>
            {/* Alert for failure */}
            <div
              className={`alert alert-${alert.type} ${
                alert.visible ? "" : "d-none"
              } fade show`}
              role="alert"
              style={{ marginTop: "5rem" }}
            >
              {alert.message}
            </div>

            <h1 className="display-6 text-center mt-2">
              Detect AI Generated texts
            </h1>
            <p className="text-center lead mt-4">
              This app helps you to identify whether a piece of text was
              generated by artificial intelligence, such as language models like
              ChatGPT, Bard, Jasper, Grammarly, GPT-4 or GPT-3. It analyzes
              patterns, phrasing, and structure that are common in AI-generated
              contents to determine the likelihood of human or machine
              authorship. This app is useful for educators, publishers, and
              content reviewers to ensure authenticity, originality, and
              transparency in digital writing.
              <strong>
                This AI detector is the best AI generated text detector
                available for free online.
              </strong>
            </p>
          </div>
        </div>

        <form onSubmit={onSubmitAIDetectorTextsForm}>
          <div className="container mt-5">
            {/* Mode selection */}
            <div className="bg-light p-3 rounded-2">
              <div>
                <h6 className="ms-2">Select detection level:</h6>
              </div>

              {/* Mode selection buttons */}
              <div
                className="btn-group ms-2 mt-2 flex-wrap"
                role="group"
                aria-label="Mode Selection"
                style={{ width: "100%" }}
              >
                <button
                  type="button"
                  className={
                    aiDetectorTextsValues.mode === 1
                      ? `btn btn-secondary mx-2 rounded-2 text-light my-1`
                      : `btn btn-outlined-secondary mx-2 rounded-2 border-secondary my-1`
                  }
                  onClick={(e) => {
                    //blur
                    e.target.blur();
                    //set mode
                    setAIDetectorTextsValues({
                      ...aiDetectorTextsValues,
                      mode: 1,
                    });
                  }}
                >
                  Casual
                </button>
                <button
                  type="button"
                  className={
                    aiDetectorTextsValues.mode === 2
                      ? `btn btn-secondary mx-2 rounded-2 text-light my-1`
                      : `btn btn-outlined-secondary mx-2 rounded-2 border-secondary my-1`
                  }
                  onClick={(e) => {
                    //blur
                    e.target.blur();
                    //set mode
                    setAIDetectorTextsValues({
                      ...aiDetectorTextsValues,
                      mode: 2,
                    });
                  }}
                >
                  Moderate
                </button>
                <button
                  type="button"
                  className={
                    aiDetectorTextsValues.mode === 3
                      ? `btn btn-secondary mx-2 rounded-2 text-light my-1`
                      : `btn btn-outlined-secondary mx-2 rounded-2 border-secondary my-1`
                  }
                  onClick={(e) => {
                    //blur
                    e.target.blur();
                    //set mode
                    setAIDetectorTextsValues({
                      ...aiDetectorTextsValues,
                      mode: 3,
                    });
                  }}
                >
                  Strict
                </button>
              </div>
            </div>

            <div className="position-relative mt-3">
              {/* Waiting time */}
              <div
                className={`position-absolute ${submitClicked ? "" : "d-none"}`}
                style={{
                  left: "0px",
                  right: "0px",
                  margin: "auto",
                  top: "0px",
                  bottom: "0px",
                  width: "200px",
                  height: "100px",
                }}
              >
                {/*Gray box */}
                <div className="bg-secondary rounded-3 p-1">
                  <label
                    className="fs-5 text-light text-center"
                    style={{ width: "100%" }}
                  >
                    Waiting time:
                  </label>
                  <label
                    className="fs-3 text-light text-center"
                    style={{ width: "100%" }}
                  >
                    {formatSecondsToMS(time)}
                  </label>
                </div>
                {/* Text upgrade */}
                <div className="bg-white bg-opacity-10">
                  <label className="text-muted text-center mt-2">
                    <span>
                      <button
                        className="btn btn-light"
                        type="button"
                        onClick={(e) => {
                          //blur
                          e.currentTarget.blur();
                          //go to pricing page
                          navigate("/pricing"); //Navigate to pricing page
                        }}
                      >
                        Upgrade
                      </button>
                    </span>{" "}
                    for quicker response time
                  </label>
                </div>
              </div>

              <textarea
                className="form-control m-2 shadow"
                id="enter_ai_text"
                rows="18"
                style={{ resize: "none" }}
                placeholder="Insert AI generated (English) text here..."
                onChange={onChangeAIDetectorTextsForm}
                value={aiDetectorTextsValues.enter_ai_text}
              ></textarea>
              <p
                className="text-center position-absolute ms-4 mb-2 p-2 bg-light rounded"
                style={{ bottom: "0px" }}
              >
                <span
                  className={`${
                    aiDetectorTextsValues.enter_ai_text.length <=
                    MIN_AI_TEXT_LENGTH
                      ? "text-danger"
                      : ""
                  }`}
                >
                  {aiDetectorTextsValues.enter_ai_text.length}
                </span>{" "}
                / {maxAITextLength} Characters
              </p>

              {/*  paste from clipboard */}
              <div
                className="position-absolute me-2 mb-3 d-flex bg-light"
                style={{ right: "0px", bottom: "0px" }}
              >
                <button
                  className="btn btn-outline-success btn-sm me-2"
                  style={{ right: "0px", bottom: "0px" }}
                  type="button"
                  onClick={(e) => {
                    //text from clipboard
                    navigator.clipboard
                      .readText()
                      .then((text) => {
                        //set text
                        setAIDetectorTextsValues({
                          ...aiDetectorTextsValues,
                          enter_ai_text: text,
                        });
                      })
                      .catch((err) => {
                        console.error(
                          "Failed to read clipboard contents: ",
                          err
                        );
                      });
                    // Remove focus after click
                    e.currentTarget.blur();
                  }}
                >
                  <i className="bi bi-clipboard me-lg-2 me-sm-0"></i>
                  <span className="d-none d-sm-inline">Paste Texts</span>
                </button>

                {/* clear text area */}
                <button
                  className="btn btn-outline-danger btn-sm"
                  style={{ right: "0px", top: "0px" }}
                  type="button"
                  onClick={(e) => {
                    //clear
                    setAIDetectorTextsValues({
                      enter_ai_text: "",
                      probability: 0,
                      text: "",
                      isLoaded: false,
                      detection_sentences: [],
                    });
                    // Remove focus after click
                    e.currentTarget.blur();
                  }}
                >
                  <i className="bi bi-trash3 me-lg-2 me-sm-0"></i>
                  <span className="d-none d-sm-inline">Clear Text</span>
                </button>
              </div>
            </div>
          </div>

          {/* button to detect ai generated text */}
          <div className="text-center mt-5 justify-content-center d-flex">
            <div
              className={`spinner-border text-dark me-3 align-self-center ${
                submitClicked ? "visible" : "invisible"
              }`}
              role="status"
            >
              <span className="sr-only"></span>
            </div>
            <button
              className="btn btn-success px-4 py-3 fs-5"
              disabled={submitClicked}
              type="submit"
              style={{
                backgroundColor: submitClicked
                  ? "#6c757d"
                  : ThemeButtonBGAIDetector,
              }}
              onClick={(e) => {
                //blur
                e.currentTarget.blur();
              }}
            >
              <i className="bi bi-file-text me-3"></i>
              Analyze Text
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default AIDetector;
